/** The setup wizard's left rail — a status-glyph nav list, deliberately NOT a
 *  Stepper: steps are non-linear, revisitable, status-rich and hidden when
 *  inapplicable, so a numbered one-shot funnel would misrepresent them.
 *  Sections use the sanctioned 11px tracked caps (a structural nav divider,
 *  same role as the navy rail's DIRECTORY/CATALOG). Meaning never rides on
 *  color alone — every glyph has a tooltip and the annotation carries text.
 */
import { Alert, Box, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

import { Glyph, GlyphLegend, type PreflightGlyph } from '@/features/preflight/glyphs';

import { SECTION_LABELS, type SetupSection, type SetupStepId } from './stepRegistry';
import type { SetupStep } from './useSetupState';

/** A step's status maps 1:1 onto the shared item vocabulary — the rail used to
 *  keep a second, slightly different copy of it. */
const STEP_GLYPH: Record<SetupStep['status'], PreflightGlyph> = {
  done: 'done',
  running: 'running',
  error: 'error',
  attention: 'attention',
  blocked: 'blocked',
  ready: 'ready',
  edit: 'edit',
};

interface Props {
  steps: SetupStep[];
  activeId: SetupStepId;
  onSelect: (id: SetupStepId) => void;
}

export function SetupRail({ steps, activeId, onSelect }: Props) {
  const visible = steps.filter((s) => s.visible);
  const hiddenCount = steps.length - visible.length;
  const sections = [...new Set(visible.map((s) => s.section))] as SetupSection[];
  const glyphsInUse = [...new Set(visible.map((s) => STEP_GLYPH[s.status]))];

  if (visible.length === 0) {
    return (
      <Box component="nav" aria-label="Setup steps" sx={{ p: 1.5 }}>
        <Alert severity="info" sx={{ mb: 1 }}>
          Every step is hidden — nothing on the checklist applies to this operator’s profile.
        </Alert>
      </Box>
    );
  }

  return (
    <Box component="nav" aria-label="Setup steps">
      <List dense disablePadding>
        {sections.map((section) => (
          <Box key={section} sx={{ mb: 1 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                px: 1.5,
                pt: 1,
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: 'text.secondary',
                lineHeight: '30px',
              }}
            >
              {SECTION_LABELS[section]}
            </Typography>
            {visible
              .filter((s) => s.section === section)
              .map((step) => {
                const active = step.id === activeId;
                return (
                  <ListItemButton
                    key={step.id}
                    selected={active}
                    onClick={() => onSelect(step.id as SetupStepId)}
                    aria-current={active ? 'step' : undefined}
                    sx={{ borderRadius: 1, py: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Glyph kind={STEP_GLYPH[step.status]} />
                    </ListItemIcon>
                    <ListItemText
                      primary={step.label}
                      secondary={step.secondary}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: active ? 600 : 500,
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                  </ListItemButton>
                );
              })}
          </Box>
        ))}
      </List>
      <Box sx={{ px: 1.5, pt: 1, mt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <GlyphLegend kinds={glyphsInUse} direction="column" />
        {hiddenCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {hiddenCount} step{hiddenCount === 1 ? '' : 's'} hidden — not applicable to this
            operator.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
